import { useState, useEffect, useRef, useCallback } from 'react';
import { Sidebar } from './Sidebar';
import { MobileTabBar } from './MobileTabBar';
import { MobileSidebarDrawer } from './MobileSidebarDrawer';
import { SyncNowButton, TopBar } from './TopBar';
import { MobileTimeFilter } from './MobileTimeFilter';
import { BackupAutoNotify } from './BackupAutoNotify';
import { NAV_ITEMS } from '@/data/mockData';
import { Sparkles, X, Home, Menu } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Tab {
  id: string;
  page: string;
  childId?: string;
  title: string;
}

interface AppLayoutProps {
  currentPage: string;
  onNavigate: (page: string, childId?: string) => void;
  activeChildId?: string;
  tabs: Tab[];
  activeTabId: string;
  onTabChange: (tabId: string) => void;
  onCloseTab: (tabId: string) => void;
  children: React.ReactNode;
}

export function AppLayout({
  currentPage,
  onNavigate,
  activeChildId,
  tabs,
  activeTabId,
  onTabChange,
  onCloseTab,
  children,
}: AppLayoutProps) {
  const navItem = NAV_ITEMS.find((n) => n.path === currentPage);
  const pageTitle = navItem?.label || '工作台';
  const showFab = currentPage !== 'ai-assistant';

  // AI 助手悬浮按钮位置（可拖动，自动持久化）
  const FAB_STORAGE_KEY = 'changfa_ai_fab_pos';
  const [fabPos, setFabPos] = useState<{ right: number; bottom: number }>(() => {
    try {
      const raw = localStorage.getItem(FAB_STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    const usesMobileTabBar = typeof window !== 'undefined' && window.innerWidth < 1280;
    return { right: usesMobileTabBar ? 16 : 24, bottom: usesMobileTabBar ? 80 : 24 };
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0, right: 0, bottom: 0, moved: false });

  useEffect(() => {
    try { localStorage.setItem(FAB_STORAGE_KEY, JSON.stringify(fabPos)); } catch { /* ignore */ }
  }, [fabPos]);

  const clampFab = useCallback((right: number, bottom: number) => {
    const usesMobileTabBar = window.innerWidth < 1280;
    const size = 48;
    const minBottom = usesMobileTabBar ? 72 : 8;
    return {
      right: Math.max(8, Math.min(window.innerWidth - size - 8, right)),
      bottom: Math.max(minBottom, Math.min(window.innerHeight - size - 8, bottom)),
    };
  }, []);

  useEffect(() => {
    const onResize = () => setFabPos((p) => clampFab(p.right, p.bottom));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clampFab]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!draggingRef.current) return;
      const dx = e.clientX - dragStartRef.current.x;
      const dy = dragStartRef.current.y - e.clientY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragStartRef.current.moved = true;
      setFabPos(clampFab(dragStartRef.current.right - dx, dragStartRef.current.bottom + dy));
    };
    const onUp = () => { draggingRef.current = false; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [clampFab]);

  return (
    <div className="flex h-screen overflow-hidden bg-[#F3F5F9]">
      <Sidebar currentPage={currentPage} onNavigate={onNavigate} activeChildId={activeChildId} />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 小屏顶部条（1024-1280 + 移动端）：汉堡 + 标题，仅在 <1280 显示 */}
        <div className="flex xl:hidden items-center justify-between px-3 py-2 bg-white border-b border-zinc-200 flex-shrink-0 relative z-[110]">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setDrawerOpen(true)}
              className="w-9 h-9 rounded-lg text-zinc-700 hover:bg-zinc-100 flex items-center justify-center flex-shrink-0"
              aria-label="打开菜单"
              title="打开菜单"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <div className="text-[14px] font-semibold text-zinc-900 truncate">{pageTitle}</div>
              <div className="text-[10.5px] text-zinc-400 truncate">昌发香行</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <SyncNowButton compact />
            {/* 全局时间筛选（移动端入口，与桌面端 TopBar 联动同一全局状态） */}
            <MobileTimeFilter />
            <button
              onClick={() => onNavigate('dashboard')}
              className="w-9 h-9 rounded-lg text-zinc-500 hover:bg-zinc-100 hover:text-emerald-600 flex items-center justify-center"
              aria-label="返回工作台"
              title="返回工作台"
            >
              <Home className="w-4 h-4" />
            </button>
          </div>
        </div>

        <TopBar title={pageTitle} />
        <BackupAutoNotify onNavigate={onNavigate} />

        {/* Browser-style tabs */}
        {tabs.length > 0 && (
          <div className="hidden xl:flex items-center gap-1 px-3 pt-2 pb-0 bg-white/50 border-b border-zinc-200 overflow-x-auto scrollbar-thin relative z-[110]">
            {tabs.map((tab) => {
              const isHome = tab.page === 'dashboard';
              const active = tab.id === activeTabId;
              return (
                <button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  onMouseDown={(e) => {
                    if (e.button === 1) {
                      e.preventDefault();
                      onCloseTab(tab.id);
                    }
                  }}
                  className={cn(
                    'group flex items-center gap-2 px-3 py-2 min-w-[100px] max-w-[180px] rounded-t-lg border-t border-l border-r text-[13px] transition-all select-none',
                    active
                      ? 'bg-emerald-600 border-emerald-600 text-white font-medium shadow-sm z-10'
                      : 'bg-zinc-50/80 border-transparent text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700'
                  )}
                >
                  {isHome ? <Home className="w-3.5 h-3.5 flex-shrink-0" /> : null}
                  <span className="truncate flex-1 text-left">{tab.title}</span>
                  {tabs.length > 1 && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        onCloseTab(tab.id);
                      }}
                      className={cn(
                        'w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-colors opacity-0 group-hover:opacity-100',
                        active ? 'hover:bg-emerald-500 text-emerald-100 hover:text-white' : 'hover:bg-zinc-200 text-zinc-400 hover:text-zinc-700'
                      )}
                    >
                      <X className="w-3 h-3" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <main className="flex-1 min-h-0 overflow-hidden relative pb-[56px] xl:pb-0">
          {children}
        </main>
      </div>

      <MobileSidebarDrawer
        open={drawerOpen}
        currentPage={currentPage}
        activeChildId={activeChildId}
        onClose={() => setDrawerOpen(false)}
        onNavigate={onNavigate}
      />

      {showFab && (
        <button
          onClick={() => {
            if (dragStartRef.current.moved) return;
            onNavigate('ai-assistant');
          }}
          onPointerDown={(e) => {
            draggingRef.current = true;
            dragStartRef.current = { x: e.clientX, y: e.clientY, right: fabPos.right, bottom: fabPos.bottom, moved: false };
            (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
          }}
          className="fixed rounded-full flex items-center justify-center shadow-lg z-40 transition-transform hover:scale-110 active:scale-95 bg-emerald-600 hover:bg-emerald-700 touch-none select-none"
          style={{
            right: fabPos.right,
            bottom: fabPos.bottom,
            width: '3rem',
            height: '3rem',
            boxShadow: '0 4px 14px rgba(5, 150, 105, 0.45)',
          }}
          title="AI 助手（按住拖动调整位置）"
          aria-label="AI 助手"
        >
          <Sparkles className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
        </button>
      )}

      <MobileTabBar currentPage={currentPage} activeChildId={activeChildId} onNavigate={onNavigate} />
    </div>
  );
}
